/**
 * UserStats Component
 * Displays user statistics cards at the top of the page
 * Data comes from backend - NO client-side calculation
 */

import { Users, UserCheck, Shield } from 'lucide-react';
import { useUserStats } from '../hooks/useUsers';

// ============================================
// Stat Card Component
// ============================================

interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  loading?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, loading }) => {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600 dark:text-slate-400">{title}</p>
          {loading ? (
            <div className="mt-2 h-8 w-16 animate-pulse rounded bg-gray-200 dark:bg-slate-700" />
          ) : (
            <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{value}</p>
          )}
        </div>
        <div className="rounded-lg bg-blue-500/10 p-3 text-blue-500">{icon}</div>
      </div>
    </div>
  );
};

// ============================================
// UserStats Component
// ============================================

export const UserStats: React.FC = () => {
  const { data: stats, isLoading } = useUserStats();

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
      <StatCard
        title="Total Users"
        value={stats?.totalUsers ?? 0}
        icon={<Users size={24} />}
        loading={isLoading}
      />
      <StatCard
        title="Active Users"
        value={stats?.activeUsers ?? 0}
        icon={<UserCheck size={24} />}
        loading={isLoading}
      />
      <StatCard
        title="Admin Users"
        value={stats?.adminUsers ?? 0}
        icon={<Shield size={24} />}
        loading={isLoading}
      />
    </div>
  );
};
